import { isNativeMiloraWebView, triggerTextFileDownload } from '@/lib/trigger-download';

export type ShareVCardResult = 'shared' | 'cancelled' | 'failed';

function ensureVcfFilename(filename: string): string {
  const trimmed = filename.trim() || 'milora-contact';
  return trimmed.toLowerCase().endsWith('.vcf') ? trimmed : `${trimmed}.vcf`;
}

function createVCardFile(vCard: string, filename: string): File | null {
  if (typeof File === 'undefined') return null;
  try {
    return new File([vCard], ensureVcfFilename(filename), { type: 'text/vcard' });
  } catch {
    return null;
  }
}

/** True only when a real share sheet (native or Web Share with files) is available. */
export function canShareVCard(): boolean {
  if (typeof window === 'undefined') return false;
  if (isNativeMiloraWebView()) return true;

  if (typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') {
    return false;
  }

  const probe = createVCardFile('BEGIN:VCARD\r\nVERSION:3.0\r\nEND:VCARD', 'probe.vcf');
  if (!probe) return false;

  try {
    return navigator.canShare({ files: [probe] });
  } catch {
    return false;
  }
}

export async function shareVCard(options: {
  vCard: string;
  filename: string;
  title: string;
}): Promise<ShareVCardResult> {
  const { vCard, filename, title } = options;
  const safeFilename = ensureVcfFilename(filename.replace(/[/\\]/g, '_').replace(/\s+/g, '-'));

  if (isNativeMiloraWebView()) {
    triggerTextFileDownload(safeFilename, vCard, 'text/vcard;charset=utf-8');
    return 'shared';
  }

  const file = createVCardFile(vCard, safeFilename);
  if (!file || typeof navigator.share !== 'function') {
    return 'failed';
  }

  if (typeof navigator.canShare === 'function' && !navigator.canShare({ files: [file] })) {
    return 'failed';
  }

  try {
    await navigator.share({ title, files: [file] });
    return 'shared';
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return 'cancelled';
    return 'failed';
  }
}
